import { productBalanceApi } from '../api/api';

/** Same shape as StockManagement — `list_of_item` array or Laravel paginator. */
export function parseProductBalanceIlist(data) {
  const raw = data?.returnData?.list_of_item;
  if (!raw) {
    return {
      items: [],
      total: 0,
      lastPage: 1,
      from: 0,
      to: 0,
      mode: 'empty',
    };
  }
  if (Array.isArray(raw)) {
    const n = raw.length;
    return {
      items: raw,
      total: n,
      lastPage: 1,
      from: n ? 1 : 0,
      to: n,
      mode: 'array',
    };
  }
  if (raw.data && Array.isArray(raw.data)) {
    return {
      items: raw.data,
      total: raw.total ?? raw.data.length,
      lastPage: Math.max(1, raw.last_page ?? 1),
      from: raw.from ?? 0,
      to: raw.to ?? 0,
      mode: 'paginator',
    };
  }
  return {
    items: [],
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
    mode: 'empty',
  };
}

/**
 * Latest balance row per product id (highest record id), scanning all list pages with no search.
 */
export async function findLatestBalanceRecordForProduct(productId) {
  const map = await findLatestBalanceRecordsForProducts([productId]);
  return map.get(String(productId)) ?? null;
}

/**
 * @param {number[]} productIds Server product ids
 * @returns {Promise<Map<string, object>>} Map of product id string -> raw balance row
 */
export async function findLatestBalanceRecordsForProducts(productIds) {
  const want = new Set(productIds.map((id) => String(id)));
  const best = new Map();
  let page = 1;
  const per = 100;
  for (let guard = 0; guard < 100; guard++) {
    const resp = await productBalanceApi.ilist({
      paginate: true,
      per_page: per,
      page,
      q: '',
    });
    const data = resp?.data ?? null;
    if (!data || data.status !== 'OK') break;
    const parsed = parseProductBalanceIlist(data);
    const items = parsed.items;
    for (const r of items) {
      const pid = String(r.product_id ?? r.product?.id ?? '');
      if (!want.has(pid)) continue;
      const prev = best.get(pid);
      if (!prev || Number(r.id) > Number(prev.id)) best.set(pid, r);
    }
    if (parsed.mode === 'array') {
      if (items.length < per) break;
      page += 1;
      continue;
    }
    if (items.length < per || page >= parsed.lastPage) break;
    page += 1;
  }
  return best;
}

export async function setProductBalanceOnServer(productId, balance) {
  const existing = await findLatestBalanceRecordForProduct(productId);
  const resp =
    existing?.id != null
      ? await productBalanceApi.update({
          id: existing.id,
          product_id: productId,
          balance,
          ...(existing.uuid ? { uuid: existing.uuid } : {}),
        })
      : await productBalanceApi.save({
          product_id: productId,
          balance,
        });
  return resp;
}

/**
 * Push new balances after a sale or stock adjustment (one batched list scan + updates).
 * @param {Array<{ serverProductId: number, newBalance: number }>} rows
 */
export async function syncProductBalancesToServer(rows) {
  if (!rows.length) return { ok: true, failures: [] };

  const ids = [...new Set(rows.map((r) => r.serverProductId))];
  const latestByProduct = await findLatestBalanceRecordsForProducts(ids);
  const failures = [];

  for (const { serverProductId, newBalance } of rows) {
    const key = String(serverProductId);
    const existing = latestByProduct.get(key) ?? null;
    try {
      const resp =
        existing?.id != null
          ? await productBalanceApi.update({
              id: existing.id,
              product_id: serverProductId,
              balance: newBalance,
              ...(existing.uuid ? { uuid: existing.uuid } : {}),
            })
          : await productBalanceApi.save({
              product_id: serverProductId,
              balance: newBalance,
            });
      const data = resp?.data;
      if (!data || data.status !== 'OK') {
        failures.push({
          serverProductId,
          message: data?.errorMessage || 'Could not update balance.',
        });
      }
    } catch (err) {
      failures.push({
        serverProductId,
        message:
          err?.response?.data?.errorMessage ||
          err?.message ||
          'Network error while updating balance.',
      });
    }
  }

  return { ok: failures.length === 0, failures };
}
